/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { getAgreementTermById } from 'api/agreement-term/agreement-term.api';
import { usePermissions, ModuleEnum, PermissionType } from 'contexts/auth/permission_context';
import { useState } from 'react';
import { ROUTES } from 'routes/constants';
import { formatDate } from 'utils/format_date';

interface IfileDownload {
  base64: string;
  name: string;
  documenyType: string;
}

export default function CardViewTermoAdesao({
  term,
  selectedUsers,
  handleSelectTerm,
  handleClick,
  handleClose,
  open,
  anchorEl,
  navigate,
}: {
  term: any;
  selectedUsers: any[];
  handleSelectTerm: (uuid: string) => void;
  handleClick: any;
  handleClose: () => void;
  open: boolean;
  anchorEl: HTMLElement | null;
  navigate: (path: string) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission } = usePermissions();

  const hasAnyAction =
    hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.READ) ||
    hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.UPDATE);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const response = await getAgreementTermById(term.uuid);
      console.log(response);

      const file: IfileDownload = response.file as any;

      if (file && file.base64) {
        const byteCharacters = atob(file.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download =
          file.name || `termo-adesao-${formatDate(term.signatureAt)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper
      key={term.uuid}
      sx={{
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '12px',
      }}
    >
      {/* Conteúdo Principal */}
      <Box
        sx={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Checkbox
            checked={selectedUsers.includes(term.uuid)}
            onChange={() => handleSelectTerm(term.uuid)}
          />
          <Box>
            <Typography variant='h6' fontWeight='bold'>
              {term.institution.name}
            </Typography>
            <Typography fontSize='14px' color='#666'>
              Data da Assinatura: {formatDate(term.signatureAt)}
            </Typography>
            <Typography fontSize='14px' color='#666'>
              Data do Vencimento: {formatDate(term.dueAt)}
            </Typography>
          </Box>
        </Box>

        {hasAnyAction && (
          <Button
            variant='outlined'
            onClick={handleClick}
            endIcon={<ArrowDropDownIcon />}
            sx={{
              height: '36px',
              minWidth: '120px',
              borderRadius: '8px',
              fontWeight: 'bold',
              color: '#1976D2',
              border: '1px solid #1976D2',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: '#E3F2FD',
                border: '1px solid #1565C0',
              },
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Aguarde...' : 'AÇÕES'}
          </Button>
        )}

        {/* Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: '8px',
              minWidth: '200px',
              marginTop: '6px',
            },
          }}
        >
          {hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.READ) && (
            <MenuItem
              onClick={() =>
                navigate(ROUTES.VIEWTERMSADHESION.replace(':uuid', term.uuid))
              }
            >
              Visualizar
            </MenuItem>
          )}
          {hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.UPDATE) && (
            <MenuItem
              onClick={() =>
                navigate(ROUTES.EDITTETERMSADHESION.replace(':uuid', term.uuid))
              }
            >
              Editar
            </MenuItem>
          )}
          {hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.UPDATE) && (
            <MenuItem
              onClick={() =>
                navigate(ROUTES.VALIDATETERMSADHESION.replace(':uuid', term.uuid))
              }
            >
              Validar
            </MenuItem>
          )}
          {hasPermission(ModuleEnum.ADHESION_TERM, PermissionType.READ) && (
            <MenuItem onClick={() => handleDownload()}>
              Baixar Termo de Adesão
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Status do Termo */}
      <Box
        sx={{
          backgroundColor: `${
            term.status === 'ATIVO'
              ? '#EAF2EA'
              : term.status === 'INATIVO'
              ? '#D32F2F0A'
              : '#ED6C020A'
          }`,
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 'bold',
          fontSize: '14px',
          color: `${
            term.status === 'ATIVO'
              ? '#123214'
              : term.status === 'INATIVO'
              ? '#D32F2F'
              : '#ED6C02'
          }`,
        }}
      >
        {term.status === 'Ativo' && '✔️'}
        {term.status === 'Bloqueado' && '⚠️'}
        {term.status === 'Inativo' && '❗'}
        <Typography fontWeight='400'>{`Situação: ${term.status}`}</Typography>
      </Box>
    </Paper>
  );
}
